import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Card, Col, Empty, Modal, Row, Table, Tag, Typography } from 'antd';
import { IDentalTreatment, IProfessional } from '../../../../shared/interfaces';
import api from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';
import { formatDate } from '../../../../shared/utils/format-date';
import { ARate, AButton, Loading } from '../../../../components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from 'chart.js';
import { Line, getElementAtEvent } from 'react-chartjs-2';
import { EyeOutlined } from '@ant-design/icons';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

interface IProps {
  open: boolean;
  close: () => void;
  treatment: IDentalTreatment | null;
}

interface IHistoric {
  id: string;
  date: string;
  time: string;
  observation: string;
  rank: number;
  status: number;
  professional_id?: string;
  signed_professional_name?: string;
  signature_validation_code?: string;
  signed_at?: string;
  created_at: string;
}

interface IChartHistoricPoint extends IHistoric {
  x: string;
  y: number;
}

const HistoricTreatment: React.FC<IProps> = ({ open, close, treatment }) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [historic, setHistoric] = useState<IHistoric[]>([]);
  const [selectedLog, setSelectedLog] = useState<IHistoric | null>(null);
  const [modalDetails, setModalDetails] = useState(false);
  const chartRef = useRef<any>(null);

  const statusLabel: Record<number, string> = {
    0: 'Pendente',
    1: 'Em andamento',
    2: 'Concluído',
  };

  const statusColor: Record<number, string> = {
    0: 'default',
    1: 'processing',
    2: 'success',
  };

  const loadHistoric = useCallback(async () => {
    if (!treatment) return;
    setLoading(true);
    try {
      const [response, professionalsResponse] = await Promise.all([
        api.get(`/dental-treatments-logs/list/${treatment.id}`),
        api.get('/professionals/select'),
      ]);
      const professionals = professionalsResponse.data as IProfessional[];

      const normalizedHistoric: IHistoric[] = (response.data || []).map(
        (item: any) => ({
          id: item.id,
          date: item.date || item.created_at,
          time: item.time || '',
          observation: item.observation || item.description || '-',
          rank: Number(item.rank ?? 0),
          status: Number(item.status ?? 0),
          professional_id: item.professional_id,
          signed_professional_name:
            professionals.find(
              (prof) => prof.id === (item.professional_id as string),
            )?.name || undefined,
          signature_validation_code: item.signature_validation_code,
          signed_at: item.signed_at,
          created_at: item.created_at,
        }),
      );
      setHistoric(normalizedHistoric);
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description:
          err?.response?.data?.message || 'Erro ao carregar o histórico.',
      });
    } finally {
      setLoading(false);
    }
  }, [treatment, addToast]);

  const sortedHistoric = useMemo(
    () =>
      [...historic].sort((a, b) => {
        const first = new Date(`${a.date}T${a.time || '00:00:00'}`).valueOf();
        const second = new Date(`${b.date}T${b.time || '00:00:00'}`).valueOf();
        return first - second;
      }),
    [historic],
  );

  const averageRank = useMemo(() => {
    if (!historic.length) return 0;
    const total = historic.reduce(
      (acc, item) => acc + Number(item.rank || 0),
      0,
    );
    return total / historic.length;
  }, [historic]);

  const latestLog = useMemo(
    () => sortedHistoric[sortedHistoric.length - 1],
    [sortedHistoric],
  );

  const selectedSignatureCode = selectedLog?.signature_validation_code;
  const selectedSignedAt = selectedLog?.signed_at
    ? formatDate(selectedLog.signed_at, 'DD/MM/YYYY HH:mm:ss')
    : null;

  const chartData: ChartData<'line', IChartHistoricPoint[]> = useMemo(
    () => ({
      labels: sortedHistoric.map((log) =>
        `${formatDate(log.date)} ${log.time || ''}`.trim(),
      ),
      datasets: [
        {
          label: 'Rank da evolução',
          data: sortedHistoric.map((log) => ({
            ...log,
            x: `${formatDate(log.date)} ${log.time || ''}`.trim(),
            y: Number(log.rank || 0),
          })),
          borderColor: 'rgb(22, 119, 255)',
          backgroundColor: 'rgba(22, 119, 255, 0.25)',
          tension: 0.3,
          pointRadius: 5,
          pointHoverRadius: 7,
        },
      ],
    }),
    [sortedHistoric],
  );

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest',
      intersect: true,
    },
    scales: {
      y: {
        max: 10,
        min: 0,
        ticks: {
          stepSize: 1,
        },
      },
    },
    plugins: {
      legend: {
        display: true,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const raw = context.raw as IChartHistoricPoint;
            const status = statusLabel[Number(raw?.status) || 0];
            return `Rank: ${raw?.y ?? 0} | Status: ${status}`;
          },
          afterLabel: (context) => {
            const raw = context.raw as IChartHistoricPoint;
            return `Obs.: ${raw?.observation || '-'}`;
          },
        },
      },
    },
  };

  const handleChartClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
      if (!chartRef.current) return;
      const points = getElementAtEvent(chartRef.current, event);
      if (!points.length) return;

      const point = points[0];
      const raw = chartData.datasets[0].data[
        point.index
      ] as IChartHistoricPoint;
      if (!raw) return;

      setSelectedLog(raw);
      setModalDetails(true);
    },
    [chartData],
  );

  useEffect(() => {
    if (open) {
      loadHistoric();
    }
  }, [open, loadHistoric]);

  return (
    <Modal
      title="Histórico do Tratamento"
      open={open}
      onCancel={close}
      footer={null}
      width={800}
      destroyOnClose
    >
      {loading ? (
        <Loading load={loading} />
      ) : (
        <>
          {!historic.length ? (
            <Empty description="Nenhum registro de evolução encontrado" />
          ) : (
            <>
              <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Typography.Text type="secondary">
                      Registros
                    </Typography.Text>
                    <Typography.Title level={3} style={{ margin: 0 }}>
                      {historic.length}
                    </Typography.Title>
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Typography.Text type="secondary">
                      Rank médio
                    </Typography.Text>
                    <Typography.Title level={3} style={{ margin: 0 }}>
                      {averageRank.toFixed(1)}
                    </Typography.Title>
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Typography.Text type="secondary">
                      Último rank
                    </Typography.Text>
                    <Typography.Title level={3} style={{ margin: 0 }}>
                      {latestLog?.rank ?? 0}
                    </Typography.Title>
                  </Card>
                </Col>
              </Row>

              <Card
                title="Gráfico da evolução"
                size="small"
                style={{ marginBottom: 16 }}
                bodyStyle={{ height: 280 }}
              >
                <Line
                  ref={chartRef}
                  options={chartOptions}
                  data={chartData}
                  onClick={handleChartClick}
                />
              </Card>

              <Table
                dataSource={sortedHistoric}
                rowKey="id"
                pagination={false}
                scroll={{ y: 260 }}
                columns={[
                  {
                    title: 'Data',
                    dataIndex: 'date',
                    width: 110,
                    render: (value: string) => formatDate(value),
                  },
                  {
                    title: 'Hora',
                    dataIndex: 'time',
                    width: 90,
                    render: (value: string) => value || '-',
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    width: 120,
                    render: (value: number) => (
                      <Tag color={statusColor[value] || 'default'}>
                        {statusLabel[value] || 'Pendente'}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Rank',
                    dataIndex: 'rank',
                    width: 210,
                    render: (value: number) => (
                      <ARate
                        value={Number(value || 0)}
                        tooltips={[
                          '1',
                          '2',
                          '3',
                          '4',
                          '5',
                          '6',
                          '7',
                          '8',
                          '9',
                          '10',
                        ]}
                        count={10}
                        disabled
                      />
                    ),
                  },
                  {
                    title: 'Observação',
                    dataIndex: 'observation',
                    render: (value: string) => value || '-',
                  },
                  {
                    title: 'Ações',
                    width: 100,
                    render: (row: IHistoric) => (
                      <AButton
                        icon={<EyeOutlined />}
                        onClick={() => {
                          setSelectedLog(row);
                          setModalDetails(true);
                        }}
                        title="Visualizar"
                      />
                    ),
                  },
                ]}
              />
            </>
          )}
        </>
      )}

      <Modal
        title="Detalhes da evolução"
        open={modalDetails}
        onCancel={() => setModalDetails(false)}
        footer={null}
      >
        <Row style={{ marginBottom: 8 }}>
          <Typography.Text strong>Data:</Typography.Text>
          <Typography.Text style={{ marginLeft: 8 }}>
            {selectedLog?.date ? formatDate(selectedLog.date) : '-'}
          </Typography.Text>
        </Row>
        <Row style={{ marginBottom: 8 }}>
          <Typography.Text strong>Hora:</Typography.Text>
          <Typography.Text style={{ marginLeft: 8 }}>
            {selectedLog?.time || '-'}
          </Typography.Text>
        </Row>
        <Row style={{ marginBottom: 8 }}>
          <Typography.Text strong>Status:</Typography.Text>
          <Typography.Text style={{ marginLeft: 8 }}>
            {statusLabel[selectedLog?.status || 0]}
          </Typography.Text>
        </Row>
        <Row style={{ marginBottom: 8, alignItems: 'center' }}>
          <Typography.Text strong>Rank:</Typography.Text>
          <div style={{ marginLeft: 8 }}>
            <ARate
              value={Number(selectedLog?.rank || 0)}
              tooltips={['1', '2', '3', '4', '5', '6', '7', '8', '9', '10']}
              count={10}
              disabled
            />
          </div>
        </Row>
        <Row>
          <Typography.Text strong>Observação:</Typography.Text>
        </Row>
        <Row>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {selectedLog?.observation || '-'}
          </Typography.Paragraph>
        </Row>
        {(selectedSignatureCode || selectedSignedAt) && (
          <Card
            size="small"
            style={{
              marginTop: 16,
              borderColor: '#92d7b1',
              background: '#eefaf3',
            }}
          >
            <Typography.Text
              strong
              style={{ display: 'block', color: '#1f7a4c', marginBottom: 4 }}
            >
              Assinatura digital
            </Typography.Text>
            <Typography.Text style={{ display: 'block', marginBottom: 4 }}>
              Assinado digitalmente por{' '}
              {selectedLog?.signed_professional_name ||
                treatment?.professional_name ||
                'profissional vinculado'}
            </Typography.Text>
            {selectedSignatureCode && (
              <Typography.Text style={{ display: 'block', marginBottom: 2 }}>
                Codigo de validacao: {selectedSignatureCode}
              </Typography.Text>
            )}
            {selectedSignedAt && (
              <Typography.Text style={{ display: 'block' }}>
                Data da assinatura: {selectedSignedAt}
              </Typography.Text>
            )}
          </Card>
        )}
      </Modal>
    </Modal>
  );
};

export default HistoricTreatment;
